import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import howIEatService from '../../services/howIEatService'
import { HowIEatProfile } from '../../types/howIEat'
import { useTranslation } from '../../hooks/use-i18n'

const HowIEatPlanTab: React.FC = () => {
  const { t } = useTranslation()
  const [profile, setProfile] = useState<HowIEatProfile | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    howIEatService.getProfile().then(setProfile).catch(() => toast.error(t('toast.settingsLoadFailed')))
  }, [t])

  const save = async () => {
    if (!profile) return
    const fastingTargetHours = Math.min(24, Math.max(8, profile.fastingTargetHours || 16))
    const reminderHour = Math.min(23, Math.max(0, profile.reminderHour ?? 9))
    try {
      setIsSaving(true)
      const updated = await howIEatService.updateProfile({
        dailyCalorieGoal: profile.dailyCalorieGoal,
        dailyProteinGoalG: profile.dailyProteinGoalG,
        dailyCarbsGoalG: profile.dailyCarbsGoalG,
        dailyFatsGoalG: profile.dailyFatsGoalG,
        dailyWaterGoalMl: profile.dailyWaterGoalMl,
        goalWeightKg: profile.goalWeightKg ?? undefined,
        heightCm: profile.heightCm ?? undefined,
        reminderEmailEnabled: profile.reminderEmailEnabled,
        reminderHour,
        fastingTargetHours,
      })
      setProfile(updated)
      toast.success(t('toast.settingsSaved'))
    } catch {
      toast.error(t('toast.settingsSaveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  if (!profile) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500" />
      </div>
    )
  }

  const numericField = (
    label: string,
    key: Exclude<keyof HowIEatProfile, 'id' | 'userId' | 'updatedAt' | 'reminderEmailEnabled'>,
    optional = false,
  ) => (
    <div className="mb-4">
      <label className="block text-white/70 text-sm mb-1">{label}</label>
      <input
        type="number"
        value={profile[key] ?? ''}
        onChange={(e) =>
          setProfile({
            ...profile,
            [key]: e.target.value === '' ? (optional ? null : 0) : Number(e.target.value),
          })
        }
        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
      />
    </div>
  )

  return (
    <div>
      <p className="text-white/50 text-sm mb-6">{t('howIEat.settings.subtitle')}</p>

      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-5 mb-4">
        <h2 className="text-white font-semibold mb-4">{t('howIEat.settings.dailyGoals')}</h2>
        {numericField(t('howIEat.settings.dailyCalories'), 'dailyCalorieGoal')}
        {numericField(t('howIEat.settings.protein'), 'dailyProteinGoalG')}
        {numericField(t('howIEat.settings.carbs'), 'dailyCarbsGoalG')}
        {numericField(t('howIEat.settings.fats'), 'dailyFatsGoalG')}
        {numericField(t('howIEat.settings.water'), 'dailyWaterGoalMl')}
        {numericField(t('howIEat.settings.goalWeight'), 'goalWeightKg', true)}
        {numericField(t('howIEat.settings.height'), 'heightCm', true)}
      </div>

      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-5 mb-4">
        <h2 className="text-white font-semibold mb-4">{t('howIEat.settings.fastingTitle')}</h2>
        {numericField(t('howIEat.settings.fastingWindow'), 'fastingTargetHours')}
      </div>

      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-5 mb-6">
        <h2 className="text-white font-semibold mb-4">{t('howIEat.settings.emailReminders')}</h2>
        <label className="flex items-center gap-3 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={profile.reminderEmailEnabled}
            onChange={(e) => setProfile({ ...profile, reminderEmailEnabled: e.target.checked })}
            className="w-4 h-4"
          />
          <span className="text-white/80 text-sm">{t('howIEat.settings.reminderToggle')}</span>
        </label>
        <label className="block text-white/70 text-sm mb-1">{t('howIEat.settings.reminderHour')}</label>
        <input
          type="number"
          min={0}
          max={23}
          value={profile.reminderHour}
          onChange={(e) => setProfile({ ...profile, reminderHour: Number(e.target.value) })}
          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
        />
      </div>

      <button
        type="button"
        disabled={isSaving}
        onClick={save}
        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-xl font-semibold disabled:opacity-60"
      >
        {isSaving ? t('howIEat.settings.saving') : t('howIEat.settings.save')}
      </button>
    </div>
  )
}

export default HowIEatPlanTab
